import { addBreadcrumb } from '../breadcrumbs/breadcrumbs';
import { reportError } from '../errors/errorReporter';

export interface DroppedMail {
  format: 'msg' | 'eml';
  contentBase64: string;
  name?: string;
}

async function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      } else reject(new Error('Unexpected FileReader result'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

function inferFormat(name: string): 'msg' | 'eml' {
  return name.toLowerCase().endsWith('.msg') ? 'msg' : 'eml';
}

export function extractDroppedFiles(e: DragEvent): File[] {
  const dt = e.dataTransfer;
  if (!dt) return [];
  const files: File[] = [];
  for (const f of Array.from(dt.files ?? [])) files.push(f);
  return files;
}

export async function filesToDroppedMails(files: File[]): Promise<DroppedMail[]> {
  const out: DroppedMail[] = [];
  for (const f of files) {
    out.push({
      format: inferFormat(f.name),
      contentBase64: await fileToBase64(f),
      name: f.name,
    });
  }
  return out;
}

// Shape of Office.DragAndDropEventArgs (web + new Outlook). The event fires on
// BOTH dragover and drop; only the drop carries the transferred items, and a
// dropped message arrives as a .eml Blob in dataTransfer.files — there is NO
// itemId (the previous code read `args.itemIds`, which never existed, so drops
// silently did nothing).
interface OfficeDroppedFile {
  name: string;
  type: string;
  fileContent: Blob;
}
interface OfficeDragAndDropEventArgs {
  dragAndDropEventData?: {
    type?: string; // 'drop' | 'dragover'
    // Cursor position relative to the add-in's task pane (i.e. our document
    // viewport) — present on new Outlook / web. Lets us hit-test the folder
    // under the cursor. `pageX/pageY` (relative to the whole Outlook window)
    // are NOT usable for that, so we only read the task-pane ones.
    taskPaneX?: number;
    taskPaneY?: number;
    dataTransfer?: { files?: OfficeDroppedFile[] };
  };
}

/** Cursor position within the task pane, when the host provides it. */
export interface DropPoint {
  x: number;
  y: number;
}

export interface OfficeDragDropCallbacks {
  /** Fires (repeatedly) while a mail is dragged over the task pane. */
  onDragOver?: (pos: DropPoint | null) => void;
  /** Fires once when mails are dropped into the task pane. */
  onDrop: (mails: DroppedMail[], pos: DropPoint | null) => Promise<void> | void;
}

// Pull the task-pane-relative cursor point out of the event, if present.
function pointFrom(data: {
  taskPaneX?: number;
  taskPaneY?: number;
}): DropPoint | null {
  return typeof data.taskPaneX === 'number' && typeof data.taskPaneY === 'number'
    ? { x: data.taskPaneX, y: data.taskPaneY }
    : null;
}

let installed = false;
// The handler is installed once (Office has no removeHandler for this event),
// but the React callbacks it invokes change across renders. Keep the latest in
// a module ref so the singleton handler never calls a stale closure.
let current: OfficeDragDropCallbacks | null = null;

export function installOfficeDragDropHandler(callbacks: OfficeDragDropCallbacks): void {
  current = callbacks;
  if (installed) return;
  // Office may not be initialized yet (the app now renders before
  // Office.onReady). Access everything defensively — Office.EventType itself
  // can be undefined, which previously threw "Cannot read properties of
  // undefined (reading 'DragAndDropEvent')" and blanked the whole pane.
  const office = (globalThis as { Office?: typeof Office }).Office;
  const eventType = office?.EventType
    ? (office.EventType as unknown as Record<string, string>)['DragAndDropEvent']
    : undefined;
  const mailbox = office?.context?.mailbox;
  if (!eventType || !mailbox?.addHandlerAsync) {
    addBreadcrumb({
      category: 'office.call',
      message: 'DragAndDropEvent not supported on this host',
    });
    return;
  }
  // Claim the slot SYNCHRONOUSLY, before the async addHandlerAsync round-trip.
  // Office accumulates handlers for this event (no removeHandler), and the app
  // mounts before Office.onReady — so several queued install() calls can run in
  // the same microtask flush and all pass the `installed` check before the
  // completion callback flips it, registering the handler 2+ times and filing
  // every dropped mail twice. Flipping it here closes that window; on a genuine
  // registration failure we reset it so a later attempt can retry.
  installed = true;
  mailbox.addHandlerAsync(
    eventType as unknown as Office.EventType,
    async (eventArgs: unknown) => {
      try {
        const data = (eventArgs as OfficeDragAndDropEventArgs).dragAndDropEventData;
        if (!data) return;
        // The event fires continuously on dragover and once on drop. Surface
        // dragover so the UI can show a "drop here" affordance (new Outlook /
        // web never raise the HTML5 dragover the classic DropZone relies on).
        const pos = pointFrom(data);
        if (data.type === 'dragover') {
          current?.onDragOver?.(pos);
          return;
        }
        if (data.type !== 'drop') return;
        const files = data.dataTransfer?.files ?? [];
        if (files.length === 0) return;
        const mails: DroppedMail[] = await Promise.all(
          files.slice(0, 100).map(async (f) => ({
            format: inferFormat(f.name),
            contentBase64: await fileToBase64(f.fileContent),
            name: f.name,
          })),
        );
        addBreadcrumb({
          category: 'office.event',
          message: 'DragAndDropEvent drop',
          data: { count: mails.length, x: pos?.x, y: pos?.y },
        });
        await current?.onDrop(mails, pos);
      } catch (err) {
        reportError({
          error: err instanceof Error ? err : new Error(String(err)),
          tags: ['dnd', 'office-handler'],
        });
      }
    },
    (res) => {
      if (res.status === Office.AsyncResultStatus.Succeeded) {
        addBreadcrumb({ category: 'office.call', message: 'DragAndDropEvent handler installed' });
      } else {
        // Registration failed → release the slot so a later call can retry.
        installed = false;
        addBreadcrumb({
          category: 'office.call',
          message: 'DragAndDropEvent handler install failed',
          data: { error: res.error?.message },
        });
      }
    },
  );
}

export async function getItemAsEmlBase64(): Promise<string> {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;
    if (!item) {
      reject(new Error('No mailbox item in context'));
      return;
    }
    item.getAsFileAsync?.(
      { asyncContext: null },
      (result: Office.AsyncResult<string>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(result.error);
        }
      },
    );
  });
}

// Shape of the item returned by loadItemByIdAsync (Office.LoadedMessageRead).
// Typed loosely because the multi-select loaded-item APIs aren't in the
// office-js types pinned here. `internetMessageId` is the real RFC message-id
// (NOT the EWS itemId used to load it).
interface LoadedMailItem {
  internetMessageId?: string;
  getAsFileAsync?: (
    opts: unknown,
    cb: (r: Office.AsyncResult<string>) => void,
  ) => void;
  unloadAsync?: (cb: (r: Office.AsyncResult<void>) => void) => void;
}

export interface LoadedItemEml {
  base64: string;
  /**
   * The item's real RFC internet message-id, read off the loaded item. Used as
   * the per-container dedup key (message_id_rfc). May be undefined on hosts
   * that don't expose it; the edge function then falls back to parsing it out
   * of the eml headers.
   */
  internetMessageId?: string;
}

/**
 * Returns a mail item as base64-encoded EML plus its real internetMessageId,
 * loading it by its EWS id via the item multi-select API (Mailbox 1.15). This
 * is REQUIRED for filing selected mails: with multi-select — or a selection
 * that differs from the mail shown in the reading pane — reading
 * `Office.context.mailbox.item` would grab the wrong message. Per the Office.js
 * contract only one item can be loaded at a time, so we always `unloadAsync`
 * before returning, letting the caller loop over several ids safely.
 *
 * The returned `internetMessageId` is the real RFC message-id read off the
 * loaded item — NOT the EWS `itemId` passed in — so callers can use it as the
 * dedup key. Falls back to the current context item on hosts without
 * `loadItemByIdAsync` (pre-1.15), which only support single selection.
 */
export async function getItemAsEmlBase64ById(itemId: string): Promise<LoadedItemEml> {
  // Fast path: the requested item is the one already open in the reading pane.
  // Microsoft's guidance is to avoid loadItemByIdAsync (Mailbox 1.15, read/write
  // mailbox) when the context item already exposes what you need — so read it
  // directly via MessageRead.getAsFileAsync (Mailbox 1.14). This is the common
  // single-open-mail case and sidesteps the heavier multi-select API entirely.
  const contextItem = Office.context.mailbox.item as unknown as
    | {
        itemId?: string;
        internetMessageId?: string;
        getAsFileAsync?: (cb: (r: Office.AsyncResult<string>) => void) => void;
      }
    | undefined;
  if (contextItem?.itemId === itemId && contextItem.getAsFileAsync) {
    const base64 = await new Promise<string>((resolve, reject) => {
      contextItem.getAsFileAsync!((r) => {
        if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value);
        else reject(r.error);
      });
    });
    return { base64, internetMessageId: contextItem.internetMessageId };
  }

  const mailbox = Office.context.mailbox as unknown as {
    loadItemByIdAsync?: (
      id: string,
      cb: (res: Office.AsyncResult<LoadedMailItem>) => void,
    ) => void;
  };
  if (!mailbox.loadItemByIdAsync) {
    // Pre-1.15: single selection only, so the context item IS the selected one.
    const contextItem = Office.context.mailbox.item as unknown as
      | { internetMessageId?: string }
      | undefined;
    return {
      base64: await getItemAsEmlBase64(),
      internetMessageId: contextItem?.internetMessageId,
    };
  }
  const loaded = await new Promise<LoadedMailItem>((resolve, reject) => {
    mailbox.loadItemByIdAsync!(itemId, (res) => {
      if (res.status === Office.AsyncResultStatus.Succeeded) resolve(res.value);
      else reject(res.error);
    });
  });
  try {
    if (!loaded.getAsFileAsync) {
      throw new Error('getAsFileAsync unavailable on loaded item');
    }
    const base64 = await new Promise<string>((resolve, reject) => {
      loaded.getAsFileAsync!({}, (r) => {
        if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value);
        else reject(r.error);
      });
    });
    return { base64, internetMessageId: loaded.internetMessageId };
  } finally {
    // Mandatory: close this item before the next loadItemByIdAsync call.
    await new Promise<void>((resolve) => {
      if (loaded.unloadAsync) loaded.unloadAsync(() => resolve());
      else resolve();
    });
  }
}
